'use client';

/**
 * ContentTreeCard — mode10's explorer surface (cards-everywhere).
 *
 * Direct copy of mode4's `VendorListCard` shape: a flat list of bordered
 * entity CARDS, collapsed by default, each chevron-expanding into its
 * sub-tree. Mapping mirrors vendor → collection → product:
 *
 *   domain  (FAQ / Competitive / Founder Letters)  = vendor   (depth 0 card)
 *   topic                                          = collection (depth 1)
 *   question                                       = product   (depth 2 leaf)
 *
 * PURE RENDERER. The `content_set` BFF ships the tree ALREADY grouped,
 * counted, and label-formatted (domain → topic → question, each leaf
 * carrying its blocks). This component paints it as-is — no grouping, no
 * humanizing, no label lookups. Same payload the canvas answer card slices.
 * Does NOT import any legacy explorer.
 *
 * @see klyde_edge/ui_config/composition/cards/investor_relations.py — content_key="content_tree"
 * @see klyde_edge/ui_config/handlers/content.py — content_set (grouping/formatting live here)
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useMemo, useState } from 'react';
import { Loader2, XCircle, Landmark, ChevronRight, MoreVertical, ClipboardCopy, Download } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CardPlacementProvider,
  useCardExport,
  downloadCapture,
  waitForCanvasTopic,
} from '@/lib/design/card-export';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useExplorerLabels, useAffordance } from '@/lib/stores/total-matrix-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { cn } from '@/lib/utils/cn';
import { text, treeDepth as depthRamp, surfaceBase } from '@/lib/design/tokens';
// Shared hook — sends `lang` to content_set and shares the cache with the
// canvas cards (same query key → one fetch per language).
import {
  useContentSet,
  type ContentDomain,
  type ContentTopic,
  type ContentQuestion,
} from '@/components/canvas/cards/investor_relations/use-content-set';

export function ContentTreeCard() {
  const L = useExplorerLabels('mode10');
  const { domains, isLoading, error, queryKey: rosterQueryKey } = useContentSet();

  // Collection card export — the roster. Children register their own.
  // Before early returns (Rules of Hooks).
  useCardExport(useMemo(() => ({
    label: L.tree_title ?? 'Investor Relations',
    content_key: 'content_tree',
    surface: 'explorer' as const,
    status: (domains.length || !isLoading ? 'loaded' : 'loading') as 'loading' | 'loaded',
    query_key: rosterQueryKey,
    text: domains.map((d) => d.label).join('\n'),
    json: {
      count: domains.reduce((n, d) => n + d.count, 0),
      domains: domains.map((d) => ({ domain: d.domain, label: d.label, count: d.count })),
    },
  }), [domains, isLoading, rosterQueryKey, L.tree_title]));

  if (isLoading) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'flex items-center gap-2 px-3 py-2')}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {L.tree_loading}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.body, 'px-3 py-2 flex items-center gap-2 text-destructive')}>
        <XCircle className="h-3.5 w-3.5" />
        {L.tree_error}
      </div>
    );
  }

  return (
    <div style={{ fontSize: surfaceBase.canvas }} className="space-y-1">
      <div className={cn(text.heading, 'flex items-center gap-1.5 px-2')}>
        <Landmark className="h-3.5 w-3.5" />
        {L.tree_title}
      </div>

      {domains.length === 0 ? (
        <div className={cn(text.muted, 'px-3 py-2')}>{L.tree_empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {domains.map((d) => (
            <li key={d.domain}>
              <CardPlacementProvider value={{ surface: 'explorer', slot_id: `content_domain:${d.domain}` }}>
                <DomainCard domain={d} rosterQueryKey={rosterQueryKey} />
              </CardPlacementProvider>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Domain = the vendor-equivalent: a bordered card at depth 0, collapsed
// by default, chevron-expand to its topics.
function DomainCard({
  domain,
  rosterQueryKey,
}: {
  domain: ContentDomain;
  rosterQueryKey: readonly unknown[];
}) {
  const depthClass = depthRamp[0];
  const [expanded, setExpanded] = useState(false);

  useCardExport(useMemo(() => ({
    label: domain.label,
    content_key: 'content_domain',
    treeDepth: 0,
    status: 'loaded' as const,
    query_key: rosterQueryKey,
    text: `${domain.label} — ${domain.count}`,
    json: {
      domain: domain.domain,
      count: domain.count,
      topics: domain.topics.map((t) => ({ topic: t.topic, count: t.count })),
    },
    state: { expanded },
  }), [domain, expanded, rosterQueryKey]));

  return (
    <div className="relative rounded-lg border border-border bg-card transition-colors hover:bg-accent/30">
      <div
        onClick={() => setExpanded((v) => !v)}
        className="group flex items-center gap-2 px-2.5 py-2 cursor-pointer"
      >
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="p-0.5 hover:bg-muted rounded shrink-0"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', expanded && 'rotate-90')} />
        </button>
        <span className={cn(depthClass, 'truncate flex-1')}>{domain.label}</span>
        <span className={cn(text.muted, 'shrink-0')}>{domain.count}</span>
      </div>

      {expanded && (
        <div className="ml-4 mb-1 pl-1 border-l border-border/50">
          {domain.topics.map((t) => (
            <TopicRow key={t.topic} topic={t} domainId={domain.domain} />
          ))}
        </div>
      )}
    </div>
  );
}

// Topic = collection-equivalent: depth 1. Row click ROUTES the canvas to
// this topic's answer grid (selection = the domain → its layout preset;
// graph selection carries {domain, topic} for the canvas card to resolve).
// Chevron toggles the inline question list.
function routeToTopic(
  setExplorerSelection: (s: string) => void,
  selectNode: (n: { id: string; label: string; type: string; properties: Record<string, unknown> }) => void,
  domainId: string,
  topicId: string,
  topicLabel: string,
) {
  selectNode({
    id: `${domainId}::${topicId}`,
    label: topicLabel,
    type: 'content_topic',
    properties: { domain: domainId, topic: topicId },
  });
  // The DOMAIN is the selection → the matrix routes to its layout preset.
  setExplorerSelection(domainId);
}

// The topic is the canvas-painting unit (mode1 project, mode4 vendor
// equivalent), so the kebab lives HERE — one level down from the roster —
// not on the section header. Capture is select-then-capture: route the
// canvas to THIS topic, wait for the scene to settle, then snapshot. The
// payload field stays the whole-tree content_set; the scene field reflects
// this topic — the two-field split is exactly what makes per-topic capture
// honest without a per-topic fetch.
function TopicRow({ topic, domainId }: { topic: ContentTopic; domainId: string }) {
  const depthClass = depthRamp[1];
  const [expanded, setExpanded] = useState(false);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const activeTopic = useGraphSelectionStore((s) => s.selectedNode?.properties?.topic);
  const isActive = activeTopic === topic.topic;

  // Role-gated kebab affordances (download_capture admin-only; copy
  // unrestricted). Same surface address for every topic row; visibility is
  // the actor's role projection, not the topic. undefined = hidden.
  const L = useExplorerLabels('mode10');
  const queryClient = useQueryClient();
  const lang = useLanguageStore((s) => s.lang);
  const copyTestId = useAffordance('explorer.content.copy_report');
  const downloadTestId = useAffordance('explorer.content.download_capture');
  const [menuOpen, setMenuOpen] = useState(false);

  // Copy this topic's questions as markdown — clipboard mirrors the row.
  const handleCopy = async () => {
    setMenuOpen(false);
    const md = `## ${topic.label}\n` + topic.questions.map((q) => `- ${q.title}`).join('\n');
    try { await navigator.clipboard.writeText(md); }
    catch (err) { console.error('[TopicRow] copy failed:', err); }
  };

  // Select-then-capture: paint the canvas to THIS topic if it isn't already
  // active, wait for the canvas layout card to re-resolve (no fetch fires —
  // the payload is the shared whole-tree call; only the scene settles), then
  // snapshot. Admin-gated via the affordance; capture_scene re-enforces at
  // the auth boundary regardless.
  const handleDownloadCapture = async () => {
    setMenuOpen(false);
    if (!isActive) {
      routeToTopic(setExplorerSelection, selectNode, domainId, topic.topic, topic.label);
      await waitForCanvasTopic(topic.topic, 4000);
    }
    const selectionId = `${domainId}::${topic.topic}`;
    try {
      await downloadCapture(queryClient, {
        mode: 'mode10',
        selection: selectionId,
        resolved_selection: selectionId,
        selection_source: 'explicit',
        viewer_lang: lang ?? 'en',
      });
    } catch (err) { console.error('[TopicRow] capture failed:', err); }
  };

  return (
    <div>
      <div
        onClick={() => routeToTopic(setExplorerSelection, selectNode, domainId, topic.topic, topic.label)}
        className="group flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-accent/50 rounded pl-3"
      >
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="p-0.5 hover:bg-muted rounded shrink-0"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight className={cn('h-3 w-3 transition-transform', expanded && 'rotate-90')} />
        </button>
        <span className={cn(depthClass, 'truncate flex-1', isActive && 'text-primary font-medium')}>{topic.label}</span>
        <span className={cn(text.muted, 'shrink-0')}>{topic.count}</span>
        {(copyTestId || downloadTestId) && (
          <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={L.menu_label}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100 aria-expanded:opacity-100"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} aria-hidden="true" />
                <div role="menu" className="absolute right-0 top-7 z-50 w-48 rounded-md border border-border bg-popover shadow-md py-1">
                  {copyTestId && (
                    <button
                      type="button" role="menuitem" data-testid={copyTestId} onClick={handleCopy}
                      className={cn(text.body, 'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left')}
                    >
                      <ClipboardCopy className="w-3.5 h-3.5" />
                      {L.action_copy_report}
                    </button>
                  )}
                  {downloadTestId && (
                    <button
                      type="button" role="menuitem" data-testid={downloadTestId} onClick={() => void handleDownloadCapture()}
                      className={cn(text.body, 'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left')}
                    >
                      <Download className="w-3.5 h-3.5" />
                      {L.action_download_capture}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {expanded && (
        <div className="ml-3 border-l border-border/50">
          {topic.questions.map((q) => (
            <QuestionLeaf key={q.key} entry={q} domainId={domainId} topicId={topic.topic} topicLabel={topic.label} />
          ))}
        </div>
      )}
    </div>
  );
}

// Question = leaf. Clicking one routes to its topic's answer grid (the
// question is among the cards shown) — same target as the topic row.
function QuestionLeaf({
  entry, domainId, topicId, topicLabel,
}: {
  entry: ContentQuestion; domainId: string; topicId: string; topicLabel: string;
}) {
  const depthClass = depthRamp[2];
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);

  return (
    <button
      onClick={() => routeToTopic(setExplorerSelection, selectNode, domainId, topicId, topicLabel)}
      className="w-full flex items-center gap-1 px-2 py-1 hover:bg-accent/50 rounded pl-4 text-left"
    >
      <span className={cn(depthClass, 'truncate flex-1')}>{entry.title}</span>
    </button>
  );
}
